import {
  postMailcomExportAndDelete,
  postMailcomExportConfirmDelete,
  resolveBaseUrl,
  type MailcomExportConfirmResponse,
} from '../../api/endpoints.js';

/**
 * Render the confirm-delete result.
 *
 * The previous message read `disabledAliases` / `protectedLeasedAliases` —
 * fields the hard-delete server never sends — so it printed "undefined alias
 * disabled, undefined leased alias KORUNDU" after a successful purge. Report
 * the counts the server actually returns, and surface `failures` (the server
 * deletes table-by-table without a transaction, so a mid-cascade error leaves
 * orphans that an operator has to clean up).
 */
function formatDeleteSummary(d: MailcomExportConfirmResponse): string {
  const n = (v: number | undefined): string =>
    typeof v === 'number' ? String(v) : '?';
  const head =
    `Kalıcı silme tamamlandı:\n` +
    `  • ${n(d.deletedAccounts)} parent hesap\n` +
    `  • ${n(d.deletedAliases)} alias (leased dahil)\n` +
    `  • ${n(d.deletedRenewalJobs)} yenileme işi\n` +
    `  • ${n(d.deletedSessions)} oturum\n` +
    `  • ${n(d.deletedInventoryRows)} envanter satırı\n` +
    `  • ${n(d.deletedCredentialBlobs)} kimlik kaydı`;
  const failed = d.failures ?? [];
  if (failed.length === 0) return head;
  return (
    `${head}\n\n` +
    `UYARI — ${failed.length} adım başarısız oldu, artık kayıt kalmış olabilir:\n` +
    failed.map((f) => `  • ${f.table}: ${f.error}`).join('\n') +
    `\n\nExport id: ${d.exportId} (denetim kaydında 'partial' olarak görünür).`
  );
}

/**
 * 2-step export-and-delete flow.
 *
 * Step 1: POST /export-and-delete → returns the TXT + exportId + confirmationTokenHint.
 *   - Trigger the file download in the browser (atob → Blob).
 *   - Prompt the operator to type the confirmationTokenHint (export id tail-8).
 * Step 2: POST /exports/:exportId/confirm-delete with the token.
 *   - HARD-DELETEs the parents and ALL their aliases from OUR database,
 *     cascading through renewal jobs, sessions, inventory rows and the
 *     credential vault entries.
 *
 * Two things this flow does NOT do, both deliberate:
 *   - It does not touch the aliases at the provider (mail.com / GMX). Only
 *     our own records are purged.
 *   - It does not spare leased aliases. An alias bound to an open order is
 *     deleted with the rest; that order stops receiving mail and falls into
 *     the normal timeout → cancel/refund path.
 *
 * Both used to be described the other way round in this file and in the
 * button tooltip ("soft-delete", "leased aliases protected") while the
 * server had already been switched to hard-delete — the copy understated an
 * irreversible action. The prompt below now states the real consequence.
 *
 * We keep the window.prompt chain instead of a Modal-driven state
 * machine — the flow is rarely used (operator-driven drain) and the
 * three-prompt sequence already enforces the audit reason + 8-char
 * token check the server expects.
 */
export async function runExportAndDeleteFlow(
  accountIds: readonly string[],
  reason: string,
  onRefresh: () => void,
  providerId: string = 'mailcom',
): Promise<void> {
  const baseUrl = resolveBaseUrl();
  const step1 = await postMailcomExportAndDelete({ baseUrl }, {
    accountIds: [...accountIds],
    reason,
  }, providerId);
  if (step1.kind !== 'ok') {
    const detail =
      step1.kind === 'forbidden'
        ? `forbidden (missing capability: ${step1.missingCapability ?? 'provider:drain'})`
        : step1.kind === 'error'
          ? step1.reason
          : step1.kind;
    window.alert(`Export adımı başarısız: ${detail}`);
    return;
  }
  const { exportId, csvBase64, fileName, parentCount, aliasCount, confirmationTokenHint } = step1.data;
  try {
    const binary = atob(csvBase64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    // 2026-05-28 — V1 TXT import-uyumlu (email:password:refreshToken).
    const blob = new Blob([bytes], { type: 'text/plain;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (err) {
    window.alert(`Dosya indirme başarısız: ${(err as Error).message}. Export id: ${exportId}`);
    return;
  }
  const token = window.prompt(
    `TXT indirildi (${parentCount} parent).\n\n` +
      `DİKKAT — bu işlem GERİ ALINAMAZ. Onaylarsanız kalıcı olarak silinecek:\n` +
      `  • ${parentCount} parent hesap (kimlik bilgileri dahil)\n` +
      `  • ${aliasCount} alias — AKTİF SİPARİŞE BAĞLI (leased) OLANLAR DAHİL\n` +
      `  • yenileme işleri, oturumlar, envanter kayıtları\n\n` +
      `Tek yedeğiniz az önce inen TXT dosyasıdır. Kaybetmeyin.\n\n` +
      `Silmeyi ONAYLAMAK için aşağıdaki token'ı yazın:\n${confirmationTokenHint}`,
  );
  if (token !== confirmationTokenHint) {
    window.alert('Onay token eşleşmedi. HİÇBİR ŞEY SİLİNMEDİ. Export kaydı bekliyor.');
    return;
  }
  const step2 = await postMailcomExportConfirmDelete({ baseUrl }, exportId, {
    confirmationToken: token,
  }, providerId);
  if (step2.kind !== 'ok') {
    const detail = step2.kind === 'error' ? step2.reason : step2.kind;
    window.alert(`Onay adımı başarısız: ${detail}. Export id: ${exportId}`);
    return;
  }
  window.alert(formatDeleteSummary(step2.data));
  onRefresh();
}

const EXPORT_STATUS_LABELS: Record<string, string> = {
  all: 'Tüm Statüler',
  active: 'Aktif',
  suspended: 'Draining',
  login_failed: 'Giriş Hatası',
  disabled: 'Devre Dışı',
};

/**
 * R36 — V1 parity (kopechka_v1 backend/routes/parentMails.js:1148-1262 +
 * frontend ParentMails.jsx:379-422): STATUS-based export-and-delete. The
 * operator picks a status filter; the toolbar shows the button ONLY for
 * allowed statuses (login_failed / disabled), then export+delete ALL parents
 * of that status — NO manual ID entry. The 2-step backend is unchanged:
 * step1 export (CSV inline) → browser download → operator confirm → step2
 * confirm-delete (token auto-passed; the confirm IS the deliberate step).
 */
export async function runExportAndDeleteByStatus(
  status: string,
  onRefresh: () => void,
  providerId: string = 'mailcom',
): Promise<void> {
  const label = EXPORT_STATUS_LABELS[status] ?? status;
  const baseUrl = resolveBaseUrl();
  // V1's status path has no separate reason prompt; the V2 backend requires a
  // reason for the audit row, so synthesize one (never operator-typed).
  const reason = `status-based export-and-delete: ${status} (operator UI)`;
  const step1 = await postMailcomExportAndDelete({ baseUrl }, { status, reason }, providerId);
  if (step1.kind !== 'ok') {
    const detail =
      step1.kind === 'forbidden'
        ? `eksik yetki: ${step1.missingCapability ?? 'provider:drain'}`
        : step1.kind === 'error'
          ? step1.reason
          : step1.kind;
    if (/not_found|bulunamad/i.test(detail)) {
      window.alert(`"${label}" durumunda parent mail bulunamadı.`);
    } else {
      window.alert(`Export adımı başarısız: ${detail}`);
    }
    return;
  }
  const { exportId, csvBase64, fileName, parentCount, aliasCount, confirmationTokenHint } = step1.data;
  try {
    const binary = atob(csvBase64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    // 2026-05-28 — V1 TXT import-uyumlu (email:password:refreshToken).
    const blob = new Blob([bytes], { type: 'text/plain;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (err) {
    window.alert(`Dosya indirme başarısız: ${(err as Error).message}. Export id: ${exportId}`);
    return;
  }
  // 2026-05-28 — operatör kararı: hard-delete + leased dahil. Müşteri tarafında
  // sipariş "hala bekliyor" görünür; OTP gelmeyince normal timeout ile cancel olur.
  // Customer hot path projection okur, alias yokluğu hata değildir.
  const ok = window.confirm(
    `"${label}" durumundaki ${parentCount} parent mail ve ${aliasCount} alias\n` +
      `CSV olarak indirildi ve sistemden FİZİKSEL SİLİNECEK (hard-delete).\n\n` +
      `⚠️ AKTİF SİPARİŞLİ aliaslar dahil silinir.\n` +
      `Müşteriler "OTP bekleniyor" ekranını görmeye devam eder; sipariş timeout sonra\n` +
      `otomatik cancel/refund'a düşer.\n\n` +
      `Bu işlem GERİ ALINAMAZ. Silme onaylansın mı?`,
  );
  if (!ok) {
    window.alert('Soft-delete iptal edildi. Export kaydı (CSV indirildi) bekliyor.');
    return;
  }
  const step2 = await postMailcomExportConfirmDelete({ baseUrl }, exportId, {
    confirmationToken: confirmationTokenHint,
  }, providerId);
  if (step2.kind !== 'ok') {
    const detail = step2.kind === 'error' ? step2.reason : step2.kind;
    window.alert(`Onay adımı başarısız: ${detail}. Export id: ${exportId}`);
    return;
  }
  window.alert(
    `Hard-delete tamamlandı:\n` +
      `  ${step2.data.deletedAccounts} parent silindi\n` +
      `  ${step2.data.deletedAliases ?? step2.data.disabledAliases ?? 0} alias silindi\n` +
      `  ${step2.data.deletedRenewalJobs ?? 0} renewal job silindi\n` +
      `  ${step2.data.deletedSessions ?? 0} session blob silindi\n` +
      `Aktif siparişler timeout ile kapanacak (müşteri etkilenmez).`,
  );
  onRefresh();
}

/**
 * UI shim. With a `status` (V1 parity — toolbar passes the active status
 * filter) runs the status flow with NO manual entry. Without it, falls back
 * to the legacy manual-ID prompt (backward-compatible).
 */
export function triggerExportAndDelete(onRefresh: () => void, status?: string, providerId: string = 'mailcom'): void {
  if (status && status.trim().length > 0) {
    void runExportAndDeleteByStatus(status.trim(), onRefresh, providerId);
    return;
  }
  const ids = window.prompt(
    "Export & Sil için Parent Account ID'leri virgülle ayırarak girin (max 200):",
  );
  if (!ids) return;
  const accountIds = ids
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  if (accountIds.length === 0) {
    window.alert('En az 1 account ID girilmeli.');
    return;
  }
  if (accountIds.length > 200) {
    window.alert('En fazla 200 account ID destekleniyor.');
    return;
  }
  const reason = window.prompt("Export gerekçesi (≥10 karakter, audit log'a kaydedilir):");
  if (!reason || reason.trim().length < 10) {
    window.alert('Gerekçe en az 10 karakter olmalı. İptal.');
    return;
  }
  void runExportAndDeleteFlow(accountIds, reason.trim(), onRefresh);
}
